import { useCallback, useMemo, useState, type CSSProperties } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { AssistantContext, ConversationDetail } from "@opus-dt/shared-types";
import { useAuthStore } from "@/stores/auth.store";
import { cn } from "@/lib/utils";
import { useAssistantStatus, useConversation, useSendMessage, useCancelTurn } from "../hooks";
import { useAssistantDock } from "../store";
import { useAssistantStream } from "../hooks/useAssistantStream";
import { DockHeader } from "./DockHeader";
import { ThreadView } from "./ThreadView";
import { Composer } from "./Composer";
import { ConversationList } from "./ConversationList";
import { ResizeHandle } from "./ResizeHandle";
import { dockShellClass, dockAnchored } from "./dock-modes";

// Orquestrador do dock (montado 1× no AppLayout). Resolve status/conversa/stream e
// costura send/cancel; a apresentação vive nos sub-componentes ao lado.

const LINE_PATH_RE = /\/lines\/([0-9a-f-]{36})/i;

function useScreenContext(): AssistantContext {
  const location = useLocation();
  return useMemo(() => {
    const match = LINE_PATH_RE.exec(location.pathname);
    return { screen: location.pathname, ...(match ? { currentLineId: match[1] } : {}) };
  }, [location.pathname]);
}

export function AssistantDock() {
  const { data: status } = useAssistantStatus();
  const open = useAssistantDock((s) => s.open);
  const mode = useAssistantDock((s) => s.mode);
  const size = useAssistantDock((s) => s.size);
  const activeConversationId = useAssistantDock((s) => s.activeConversationId);
  const role = useAuthStore((s) => s.user?.role ?? "OPERADOR");
  const context = useScreenContext();
  const qc = useQueryClient();

  const conversation = useConversation(open ? activeConversationId : null);
  const sendMessage = useSendMessage();
  const cancelTurn = useCancelTurn(activeConversationId);

  // Turno a streamar: o recém-enviado OU (retomada F5) o turno ativo carregado na conversa. Deriva —
  // sem effect nem setState em render.
  const [pendingTurnId, setPendingTurnId] = useState<string | null>(null);
  const [turnError, setTurnError] = useState<string | null>(null);
  const [showList, setShowList] = useState(false);
  const streamTurnId = pendingTurnId ?? conversation.data?.activeTurn?.id ?? null;

  const onTerminal = useCallback(
    (type: "done" | "error" | "canceled", message?: string) => {
      setPendingTurnId(null);
      if (type === "error") setTurnError(message ?? "O assistente está indisponível agora.");
      // Limpa o turno ativo no cache já (a invalidação reconcilia) — evita religar no turno encerrado.
      if (activeConversationId)
        qc.setQueryData<ConversationDetail>(
          ["assistant", "conversation", activeConversationId],
          (old) => (old ? { ...old, activeTurn: null } : old)
        );
    },
    [activeConversationId, qc]
  );

  const { events, phase } = useAssistantStream({
    turnId: streamTurnId,
    conversationId: activeConversationId,
    onTerminal,
  });

  const handleSend = useCallback(
    (content: string) => {
      setTurnError(null);
      sendMessage.mutate(
        { content, context },
        { onSuccess: (res) => setPendingTurnId(res.turnId) }
      );
    },
    [sendMessage, context]
  );

  const handleCancel = useCallback(() => {
    if (streamTurnId) cancelTurn.mutate(streamTurnId);
  }, [streamTurnId, cancelTurn]);

  if (!status?.enabled || !open) return null;

  return (
    <aside
      aria-label="Assistente de IA"
      style={{ "--dock-size": `${size}px` } as CSSProperties}
      className={cn(
        "fixed z-40 flex flex-col border-border bg-card shadow-lg",
        dockShellClass(mode)
      )}
    >
      {dockAnchored(mode) && <ResizeHandle mode={mode} />}
      <DockHeader
        title={conversation.data?.title ?? "Assistente"}
        showList={showList}
        onToggleList={() => setShowList((v) => !v)}
      />
      {showList ? (
        <ConversationList onPick={() => setShowList(false)} />
      ) : (
        <>
          <ThreadView
            conversation={conversation}
            events={events}
            phase={phase}
            turnError={turnError}
            role={role}
            onCancel={handleCancel}
            onExample={handleSend}
          />
          <Composer onSend={handleSend} busy={!!streamTurnId} disabled={sendMessage.isPending} />
        </>
      )}
    </aside>
  );
}
